import { use, useCallback, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import FlotiqPlugins from '../../../lib/flotiq-plugins/flotiqPluginsRegistry';
import { EMPTY_FUNCTION } from '../../../lib/globals';
import { formatErrorToString, getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import Switch from '../../../components/Switch/Switch';
import Tooltip from '../../../components/Tooltip/Tooltip';

import useDarkMode from '../../../hooks/useDarkMode';

import UserContext from '../../../contexts/UserContext';

import {
  CaretRightIcon,
  DeleteIcon,
  GithubColorIcon,
  GithubColorIconWhite,
  InformationSignIcon,
  PlusIcon,
  WarningTriangleIcon,
  WarningTriangleRedIcon,
} from '../../../images/shapes';

import './permissions-table.css';

import type { PluginContentObject } from '../../../lib/flotiq-client/types/api-response';
import type { MarketplacePlugin } from '../../../lib/flotiq-client/types/external-api-response';
import type { FlotiqPluginInfo } from '../../../lib/flotiq-plugins/types';

interface PluginItemProps {
  /**
   * Plugin data
   */
  plugin: PluginContentObject | MarketplacePlugin;
  /**
   * Plugin manifest data
   */
  manifest?: FlotiqPluginInfo;
  /**
   * On manage button click callback
   */
  openManageModal: (plugin: PluginContentObject) => void;
  /**
   * On enable switch change callback
   */
  enablePlugin: (
    plugin: PluginContentObject | MarketplacePlugin,
    enabled?: boolean,
    newValue?: boolean,
    pluginsDisabled?: boolean,
  ) => Promise<boolean>;
  /**
   * New version from plugins library
   */
  newPluginVersion?: MarketplacePlugin | null;
  /**
   * If any actions for plugins are not allowed
   */
  disabled?: boolean;
  /**
   * On plugin delete callback
   */
  onDelete?: (plugin: PluginContentObject) => void;
  /**
   * Called when user wants to update plugin
   */
  onUpdate?: (
    plugin: PluginContentObject,
    newVersion: MarketplacePlugin,
  ) => void;
  /**
   * If plugin is not added to user plugins yet
   */
  isNew?: boolean;
  /**
   * Limit reached tooltip
   */
  limitReached?: boolean;
  /**
   * If plugins are disabled
   */
  pluginsDisabled?: boolean;
  /**
   * Component test id
   */
  testId?: string;
}

const PluginItem = ({
  plugin,
  manifest,
  openManageModal,
  enablePlugin,
  newPluginVersion,
  disabled = false,
  onDelete,
  onUpdate = EMPTY_FUNCTION,
  isNew = false,
  limitReached = false,
  pluginsDisabled = false,
  testId = '',
}: PluginItemProps) => {
  const { t } = useTranslation();
  const [darkMode] = useDarkMode();
  const [opened, open] = useReducer((opened) => {
    return !opened;
  }, false);

  const { permissions } = use(UserContext);

  const { canDelete, canUpdate, canCreate } = useMemo(
    () => permissions.getCoPermissions('_plugin_settings') || {},
    [permissions],
  );

  const pluginEnabled = 'enabled' in plugin ? plugin?.enabled : false;

  const pluginErrors = useMemo(() => {
    const errors = FlotiqPlugins.getPluginErrors(plugin.id);
    return errors ? formatErrorToString(errors) : '';
  }, [plugin.id]);

  const handleDelete = useCallback(() => {
    onDelete?.(plugin as PluginContentObject);
  }, [onDelete, plugin]);

  const handleUpdate = useCallback(() => {
    if (!newPluginVersion) return;
    onUpdate?.(plugin as PluginContentObject, newPluginVersion);
  }, [newPluginVersion, onUpdate, plugin]);

  const hasManageEvent = FlotiqPlugins.hasPluginOneOfEvents(plugin.id, [
    'flotiq.plugins.manage::render',
    'flotiq.plugins.manage::form-schema',
  ]);

  const enableSwitch = useMemo(() => {
    const blocked = limitReached && !pluginEnabled && !pluginsDisabled;
    const isChecked = pluginsDisabled ? false : pluginEnabled;

    const switchButton = (
      <Switch
        name={plugin.name + '-enabled'}
        checked={isChecked}
        onChange={async (e) =>
          await enablePlugin(plugin, false, e.target.value, pluginsDisabled)
        }
        disabled={!canUpdate || blocked || disabled}
        additionalClasses="justify-items-start"
        {...getTestProps(testId, `enabled`, 'testId')}
      />
    );

    if (!canUpdate)
      return (
        <Tooltip
          tooltip={t('Global.NoAccess')}
          additionalClasses="inline-block font-normal"
          tooltipPlacement="topRight"
        >
          {switchButton}
        </Tooltip>
      );

    if (blocked)
      return (
        <Tooltip
          tooltip={t('Plugins.UpgradeToUnlock')}
          additionalClasses="inline-block font-normal"
          tooltipPlacement="topRight"
        >
          {switchButton}
        </Tooltip>
      );
    return switchButton;
  }, [
    canUpdate,
    disabled,
    enablePlugin,
    limitReached,
    plugin,
    pluginEnabled,
    pluginsDisabled,
    t,
    testId,
  ]);

  const permissionsBlock = useMemo(() => {
    if (!opened) return null;
    return (
      <div className="mt-1 md:mt-3 border-t">
        <p className="mt-1 md:mt-3 mb-2 md:ml-3 dark:text-slate-200">
          {t('Plugins.UI.AccessDescription')}
        </p>
        <table
          className="permissions-table overflow-x-overlay scrollbar-sm"
          {...getTestProps(testId, 'permissions')}
        >
          <thead>
            <tr>
              <th>{t('Plugins.UI.AccessType')}</th>
              <th>{t('Plugins.UI.ContentTypeDefinition')}</th>
              <th>{t('Plugins.UI.Operations')}</th>
            </tr>
          </thead>
          <tbody>
            {manifest?.permissions?.map((permission) => (
              <tr key={permission.ctdName + '|' + permission.type}>
                <td>{t(`Plugins.UI.Type.${permission.type}`)}</td>
                <td>{permission.ctdName || t('Plugins.UI.AllCTD')}</td>
                <td>
                  {['Read', 'Create', 'Update', 'Delete']
                    .filter(
                      (perm) =>
                        permission[`can${perm}` as keyof typeof permission],
                    )
                    .map((perm) => t(`Global.Permission.${perm}`))
                    .join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [manifest?.permissions, opened, t, testId]);

  return (
    <div className="rounded-lg bg-white dark:bg-slate-950 p-3 md:p-5 my-2">
      <div
        key={plugin.name}
        className={twMerge(
          'flex flex-col sm:flex-row sm:items-center justify-between gap-2',
        )}
      >
        <div className="flex flex-col gap-2">
          <span className="flex gap-2 items-center text-xl font-bold dark:text-slate-200">
            {plugin.name}{' '}
            {plugin.version && (
              <span className="flex gap-2 items-center">
                v{plugin.version}
                {newPluginVersion && (
                  <Tooltip
                    tooltip={t('Plugins.NewVersionTooltip', {
                      currentVersion: plugin.version,
                      newVersion: newPluginVersion.version,
                    })}
                    additionalClasses="font-normal"
                    {...getTestProps(testId, `new-version`, 'testId')}
                  >
                    <Button
                      onClick={handleUpdate}
                      buttonColor="borderless"
                      additionalClasses="px-0 text-slate-400 dark:text-slate-400"
                      iconImage={
                        <InformationSignIcon className="text-blue w-4" />
                      }
                      disabled={!canUpdate}
                      {...getTestProps(testId, `update-plugin`, 'testId')}
                    >
                      {t('Plugins.NewVersionUpdateAction')}
                    </Button>
                  </Tooltip>
                )}
              </span>
            )}
            {pluginErrors && (
              <Tooltip
                tooltip={pluginErrors}
                additionalClasses="font-normal"
                {...getTestProps(testId, `warning`, 'testId')}
              >
                <WarningTriangleRedIcon className="w-4 min-w-4" />
              </Tooltip>
            )}
          </span>
          <span className="dark:text-slate-200">
            {plugin.description || ''}
          </span>
          {(manifest?.repository ||
            (manifest?.permissions && manifest.permissions.length > 0)) && (
            <div className="flex items-between sm:items-start gap-8 mt-1 md:mt-3">
              {manifest.repository && (
                <Button
                  buttonSize="sm"
                  buttonColor="borderless"
                  iconImage={
                    darkMode ? (
                      <GithubColorIconWhite className="min-w-4 w-4" />
                    ) : (
                      <GithubColorIcon className="min-w-4 w-4" />
                    )
                  }
                  iconPosition="start"
                  as="a"
                  target="_blank"
                  rel="noreferrer"
                  href={manifest.repository}
                  additionalIconClasses="mr-2 mb-0.5"
                  {...getTestProps(testId, `repository`, 'testId')}
                >
                  {t('Plugins.UI.Details')}
                </Button>
              )}
              {manifest.permissions && manifest.permissions.length > 0 && (
                <Button
                  onClick={open}
                  buttonSize="sm"
                  buttonColor="borderless"
                  iconImage={
                    <CaretRightIcon
                      className={twMerge('w-1.5', opened && 'rotate-90')}
                    />
                  }
                  iconPosition="start"
                  additionalIconClasses="mr-0.5"
                  {...getTestProps(testId, `open-permissions`, 'testId')}
                >
                  {t('Plugins.UI.DataAccess')}
                </Button>
              )}
            </div>
          )}
          <div className="sm:hidden">{permissionsBlock}</div>
        </div>
        <div className="flex gap-4 mx-auto sm:mx-0 sm:ml-auto items-center">
          {pluginEnabled &&
            hasManageEvent &&
            canUpdate &&
            'enabled' in plugin && (
              <Button
                onClick={() => openManageModal(plugin)}
                color="blueBordered"
                additionalClasses="whitespace-nowrap ml-2"
                {...getTestProps(testId, `manage`, 'testId')}
              >
                {t('Plugins.Manage')}
              </Button>
            )}

          {pluginsDisabled && !isNew && (
            <WarningTriangleIcon className="inline-block w-4 m-auto text-red-600 dark:text-red-400" />
          )}

          {!isNew && enableSwitch}

          {isNew && canCreate && (
            <Tooltip
              tooltip={
                limitReached
                  ? t('Plugins.UpgradeToUnlock')
                  : t('Plugins.AddPlugin')
              }
              additionalClasses="inline-block font-normal"
              tooltipPlacement="topRight"
            >
              <Button
                iconImage={
                  <PlusIcon className="h-3.5 text-indigo-750 dark:text-slate-200 opacity-50 hover:opacity-100" />
                }
                buttonColor="borderless"
                onClick={() => enablePlugin(plugin)}
                disabled={limitReached || disabled}
                {...getTestProps(testId, `add`, 'testId')}
              />
            </Tooltip>
          )}
          {!isNew && onDelete && canDelete && (
            <Tooltip
              tooltip={t('Plugins.DeletePlugin')}
              additionalClasses="inline-block font-normal"
              tooltipPlacement="topRight"
              phoneTooltipPlacement="topRight"
            >
              <Button
                iconImage={
                  <DeleteIcon className="h-3.5 text-indigo-750 dark:text-slate-200 opacity-50 hover:opacity-100" />
                }
                buttonColor="borderless"
                onClick={handleDelete}
                disabled={disabled}
                {...getTestProps(testId, `delete`, 'testId')}
              />
            </Tooltip>
          )}
        </div>
      </div>
      <div className="hidden sm:block">{permissionsBlock}</div>
    </div>
  );
};

export default PluginItem;
